import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { ai, getToken } from '../api/client.js';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function FontPairings({ fontId, onSelectFont }) {
  const { theme } = useTheme();
  const [pairings, setPairings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fontsReady, setFontsReady] = useState({});

  useEffect(() => {
    if (!fontId) return;
    setLoading(true);
    setPairings([]);
    setFontsReady({});
    ai.pairings(fontId)
      .then(data => setPairings(data.pairings || []))
      .catch(() => setPairings([]))
      .finally(() => setLoading(false));
  }, [fontId]);

  // Load each paired font via FontFace API so preview renders in the actual typeface
  useEffect(() => {
    if (!pairings.length) return;
    setFontsReady({});

    pairings.forEach(async (f, idx) => {
      try {
        if (!f.preview_file_id) return;
        const resp = await fetch(
          `${API_BASE}/fonts/${f.id}/serve/${f.preview_file_id}`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        if (!resp.ok) return;
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        const fontFace = new FontFace(f.family, `url(${blobUrl})`);
        const loaded = await fontFace.load();
        document.fonts.add(loaded);
        setFontsReady(prev => ({ ...prev, [idx]: true }));
      } catch (_) {}
    });
  }, [pairings]);

  if (loading) return (
    <div style={{ color: theme.textMuted, fontSize: 11, padding: '8px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        fontSize: 12,
        background: 'linear-gradient(135deg, #7C5CFC, #4F8EF7)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}>&#10024;</span>
      Finding AI pairings...
    </div>
  );
  if (pairings.length === 0) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 10, fontWeight: 600, color: theme.textFaint,
        marginBottom: 8, letterSpacing: '0.06em', textTransform: 'uppercase',
        display: 'flex', alignItems: 'center', gap: 5,
      }}>
        <span style={{
          fontSize: 11,
          background: 'linear-gradient(135deg, #7C5CFC, #4F8EF7)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>&#10024;</span>
        AI Pairings
      </div>
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
        {pairings.map((f, idx) => (
          <button
            key={f.id}
            onClick={() => onSelectFont?.(f.id)}
            style={{
              minWidth: 150, maxWidth: 180, padding: '10px 12px',
              background: theme.bgTertiary,
              border: `1px solid ${theme.borderLight}`,
              borderRadius: 8, cursor: 'pointer',
              textAlign: 'left', flexShrink: 0,
              fontFamily: 'inherit',
              transition: 'border-color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = theme.accentBg;
              e.currentTarget.style.borderColor = theme.accentBorder;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = theme.bgTertiary;
              e.currentTarget.style.borderColor = theme.borderLight;
            }}
          >
            {/* Live font preview */}
            <div style={{
              fontFamily: `'${f.family}', serif`,
              fontSize: 22, lineHeight: 1.2, color: theme.text,
              marginBottom: 6,
              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
              opacity: fontsReady[idx] ? 1 : 0.25,
              transition: 'opacity 0.3s',
            }}>
              Aa
            </div>
            <div style={{
              fontSize: 10, fontWeight: 600, color: theme.textSecondary,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              marginBottom: 2,
            }}>{f.name || f.family}</div>
            <div style={{ fontSize: 9, color: theme.textGhost, marginBottom: 4 }}>{f.category}</div>
            {f.reason && (
              <div style={{
                fontSize: 8, color: theme.accent,
                background: theme.accentBg,
                padding: '2px 6px', borderRadius: 3,
                overflow: 'hidden', textOverflow: 'ellipsis',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              }}>{f.reason}</div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
